// Servicio para manejar los datos del RSVP con mecanismos de respaldo y mejor manejo de errores

export interface RSVPEntry {
  id: string;
  name: string;
  attendance: 'Sí asistiré' | 'No podré asistir';
  allergies: string;
  musicRecommendations: string;
  timestamp: string;
}

// Claves para almacenar los datos
const RSVP_DATA_KEY = 'rsvp_data';
const RSVP_BACKUP_KEY = 'rsvp_data_backup';

// Verificar si localStorage está disponible
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = 'test';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

// Función para guardar datos con respaldo
const saveDataWithBackup = (data: RSVPEntry[]): boolean => {
  try {
    const jsonData = JSON.stringify(data);
    
    // Guardar en localStorage
    if (isLocalStorageAvailable()) {
      localStorage.setItem(RSVP_DATA_KEY, jsonData);
      
      // Crear respaldo
      localStorage.setItem(RSVP_BACKUP_KEY, jsonData);
      return true;
    }
    
    // Si localStorage no está disponible, intentar usar sessionStorage
    sessionStorage.setItem(RSVP_DATA_KEY, jsonData);
    return true;
  } catch (error) {
    console.error('Error al guardar datos RSVP:', error);
    return false;
  }
};

// Obtener todos los registros RSVP desde el almacenamiento local
export const getAllRSVPEntries = (): RSVPEntry[] => {
  try {
    // Intentar obtener datos del almacenamiento principal
    if (isLocalStorageAvailable()) {
      const data = localStorage.getItem(RSVP_DATA_KEY);
      
      if (data) {
        try {
          return JSON.parse(data);
        } catch (parseError) {
          // Si hay error al parsear, intentar con el respaldo
          console.warn('Error al parsear datos RSVP, intentando con respaldo:', parseError);
          const backupData = localStorage.getItem(RSVP_BACKUP_KEY);
          
          if (backupData) {
            try {
              return JSON.parse(backupData);
            } catch (backupError) {
              console.error('Error al parsear respaldo RSVP:', backupError);
            }
          }
        }
      }
      
      return [];
    }
    
    // Si localStorage no está disponible, intentar sessionStorage
    const sessionData = sessionStorage.getItem(RSVP_DATA_KEY);
    return sessionData ? JSON.parse(sessionData) : [];
  } catch (error) {
    console.error('Error al obtener datos RSVP:', error);
    return [];
  }
};

// Obtener todos los registros RSVP desde Firebase (fuente principal)
export const getAllRSVPEntriesFromFirebase = async (): Promise<RSVPEntry[]> => {
  try {
    console.log('Obteniendo datos RSVP desde Firebase...');
    
    // Obtener datos desde Firebase
    const firebaseData = await getAllRSVPFromFirebase();
    
    if (firebaseData.length > 0) {
      console.log(`Datos obtenidos de Firebase: ${firebaseData.length} registros`);
      
      // Actualizar el almacenamiento local con los datos de Firebase
      saveDataWithBackup(firebaseData);
      
      return firebaseData;
    } else {
      console.log('No hay datos en Firebase, verificando almacenamiento local');
      
      // Si no hay datos en Firebase, intentar usar datos locales
      const localData = getAllRSVPEntries();
      
      if (localData.length > 0) {
        console.log(`Usando ${localData.length} registros del almacenamiento local`);
        
        // Sincronizar los datos locales con Firebase
        syncLocalDataToFirebase(localData)
          .then(success => {
            if (success) {
              console.log('Datos locales sincronizados con Firebase');
            }
          })
          .catch(error => {
            console.error('Error al sincronizar datos locales con Firebase:', error);
          });
      }
      
      return localData;
    }
  } catch (error) {
    console.error('Error al obtener datos de Firebase:', error);
    
    // En caso de error, usar datos locales como respaldo
    return getAllRSVPEntries();
  }
};

// Validar datos de entrada
const validateRSVPEntry = (entry: Omit<RSVPEntry, 'id' | 'timestamp'>): boolean => {
  if (!entry.name || entry.name.trim() === '') {
    console.error('Nombre inválido en entrada RSVP');
    return false;
  }
  
  if (!entry.attendance || !['Sí asistiré', 'No podré asistir'].includes(entry.attendance)) {
    console.error('Asistencia inválida en entrada RSVP');
    return false;
  }
  
  return true;
};

// Importar el servicio de Google Sheets
import { sendToGoogleSheet, getFromGoogleSheet } from './googleSheetService';

// Importar el servicio de Firebase
import { saveRSVPToFirebase, getAllRSVPFromFirebase, syncLocalDataToFirebase } from './firebaseService';

// Añadir un nuevo registro RSVP con validación y manejo de errores mejorado
export const addRSVPEntry = async (entry: Omit<RSVPEntry, 'id' | 'timestamp'>): Promise<RSVPEntry | null> => {
  try {
    // Validar datos de entrada
    if (!validateRSVPEntry(entry)) {
      throw new Error('Datos de RSVP inválidos');
    }
    
    // Generar un ID único y timestamp
    const id = generateId();
    const timestamp = new Date().toISOString();
    
    // Crear la entrada completa
    const newEntry: RSVPEntry = {
      id,
      timestamp,
      ...entry
    };
    
    // Guardar en Firebase (principal)
    const firebaseSaveSuccess = await saveRSVPToFirebase(newEntry);
    
    if (!firebaseSaveSuccess) {
      console.warn('No se pudo guardar en Firebase, intentando almacenamiento local');
    }
    
    // Obtener los datos actuales del almacenamiento local (respaldo)
    const currentData = getAllRSVPEntries();
    
    // Añadir la nueva entrada al almacenamiento local
    const updatedData = [...currentData, newEntry];
    
    // Guardar los datos actualizados localmente como respaldo
    const saveSuccess = saveDataWithBackup(updatedData);
    
    if (!saveSuccess) {
      console.warn('Error al guardar los datos localmente');
    }
    
    // Enviar los datos a Google Sheets (para compatibilidad con versiones anteriores)
    try {
      await sendToGoogleSheet({
        name: newEntry.name,
        attendance: newEntry.attendance,
        allergies: newEntry.allergies,
        musicRecommendations: newEntry.musicRecommendations
      });
    } catch (googleError) {
      console.warn('No se pudieron enviar los datos a Google Sheets:', googleError);
      // Continuamos aunque falle el envío a Google Sheets
    }
    
    return newEntry;
  } catch (error) {
    console.error('Error al añadir entrada RSVP:', error);
    return null;
  }
};

// Sincronizar datos con Google Sheets - versión mejorada que prioriza Google Sheets
export const syncWithGoogleSheets = async (): Promise<boolean> => {
  try {
    console.log('Iniciando sincronización con Google Sheets...');
    const googleData = await getFromGoogleSheet();
    
    if (!googleData || !Array.isArray(googleData)) {
      console.warn('No se pudieron obtener datos de Google Sheets o formato inválido');
      return false;
    }
    
    console.log(`Datos recibidos de Google Sheets: ${googleData.length} registros`);
    
    // Convertir los datos de Google Sheets al formato local
    const convertedEntries: RSVPEntry[] = googleData.map(item => ({
      id: item.id || generateId(),
      name: item.name || '',
      attendance: item.attendance === 'Sí asistiré' ? 'Sí asistiré' : 'No podré asistir',
      allergies: item.dietary || '',
      musicRecommendations: item.guestNames || '',
      timestamp: item.timestamp || new Date().toISOString()
    }));
    
    // Guardar los datos convertidos localmente
    const saveResult = saveDataWithBackup(convertedEntries);
    console.log(`Datos sincronizados guardados localmente: ${saveResult ? 'Éxito' : 'Fallo'}`);
    
    return saveResult;
  } catch (error) {
    console.error('Error al sincronizar con Google Sheets:', error);
    return false;
  }
};

// Obtener todos los registros RSVP priorizando la sincronización con Google Sheets
export const getAllRSVPEntriesWithSync = async (): Promise<RSVPEntry[]> => {
  try {
    // Intentar sincronizar con Google Sheets primero
    const syncSuccess = await syncWithGoogleSheets();
    
    if (syncSuccess) {
      console.log('Datos sincronizados correctamente, obteniendo datos actualizados.');
    } else {
      console.warn('Sincronización fallida, usando datos locales como respaldo.');
    }
    
    // Obtener los datos locales (que ahora deberían estar sincronizados)
    return getAllRSVPEntries();
  } catch (error) {
    console.error('Error al obtener datos RSVP con sincronización:', error);
    // Fallar de manera segura: devolver los datos locales
    return getAllRSVPEntries();
  }
};

// Generar un ID único más robusto
const generateId = (): string => {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2)}-${Math.random().toString(36).substring(2)}`;
};

// Obtener estadísticas de RSVP con manejo de errores
export const getRSVPStats = () => {
  try {
    const entries = getAllRSVPEntries();
    
    return {
      total: entries.length,
      attending: entries.filter(entry => entry.attendance === 'Sí asistiré').length,
      notAttending: entries.filter(entry => entry.attendance === 'No podré asistir').length
    };
  } catch (error) {
    console.error('Error al obtener estadísticas RSVP:', error);
    return {
      total: 0,
      attending: 0,
      notAttending: 0
    };
  }
};

// Filtrar entradas por criterios con manejo de errores
export const filterRSVPEntries = (
  searchTerm: string = '', 
  attendanceFilter: 'all' | 'attending' | 'not-attending' = 'all'
): RSVPEntry[] => {
  try {
    let entries = getAllRSVPEntries();
    
    // Filtrar por término de búsqueda
    if (searchTerm && searchTerm.trim() !== '') {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      entries = entries.filter(entry => 
        entry.name.toLowerCase().includes(normalizedSearchTerm)
      );
    }
    
    // Filtrar por asistencia
    if (attendanceFilter === 'attending') {
      entries = entries.filter(entry => entry.attendance === 'Sí asistiré');
    } else if (attendanceFilter === 'not-attending') {
      entries = entries.filter(entry => entry.attendance === 'No podré asistir');
    }
    
    return entries;
  } catch (error) {
    console.error('Error al filtrar entradas RSVP:', error);
    return [];
  }
};

// Exportar datos a formato CSV
export const exportToCSV = (): string => {
  try {
    const entries = getAllRSVPEntries();
    if (entries.length === 0) {
      return '';
    }
    
    // Cabeceras CSV
    const headers = ['Nombre', 'Asistencia', 'Alergias/Preferencias', 'Recomendaciones Musicales', 'Fecha'];
    
    // Convertir datos a filas CSV
    const rows = entries.map(entry => [
      entry.name,
      entry.attendance,
      entry.allergies,
      entry.musicRecommendations,
      new Date(entry.timestamp).toLocaleString('es-ES')
    ]);
    
    // Combinar cabeceras y filas
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    return csvContent;
  } catch (error) {
    console.error('Error al exportar datos a CSV:', error);
    return '';
  }
};

// Borrar todos los datos con manejo de errores
export const clearAllRSVPData = (): boolean => {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(RSVP_DATA_KEY);
      localStorage.removeItem(RSVP_BACKUP_KEY);
    }
    
    sessionStorage.removeItem(RSVP_DATA_KEY);
    return true;
  } catch (error) {
    console.error('Error al borrar datos RSVP:', error);
    return false;
  }
};

// Añadir datos de ejemplo (solo para pruebas)
export const addSampleData = (): boolean => {
  try {
    const sampleData: Omit<RSVPEntry, 'id' | 'timestamp'>[] = [
      {
        name: 'Juan Pérez',
        attendance: 'Sí asistiré',
        allergies: 'Alergia a los frutos secos',
        musicRecommendations: 'Despacito, Bailando'
      },
      {
        name: 'María García',
        attendance: 'Sí asistiré',
        allergies: '',
        musicRecommendations: 'Vivir mi vida, Danza Kuduro'
      },
      {
        name: 'Carlos Rodríguez',
        attendance: 'No podré asistir',
        allergies: '',
        musicRecommendations: ''
      },
      {
        name: 'Ana Martínez',
        attendance: 'Sí asistiré',
        allergies: 'Intolerancia a la lactosa',
        musicRecommendations: 'Gasolina, La Bicicleta'
      }
    ];
    
    // Añadir cada entrada de ejemplo
    let allSuccess = true;
    sampleData.forEach(entry => {
      const result = addRSVPEntry(entry);
      if (!result) allSuccess = false;
    });
    
    return allSuccess;
  } catch (error) {
    console.error('Error al añadir datos de ejemplo:', error);
    return false;
  }
};
